import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Fragment, useState } from "react"
import type { CellQuote, EvidenceAnchor } from "../../../../../api/types"

export interface EvidenceListProps {
  evidence?: readonly EvidenceAnchor[]
  cells?: readonly CellQuote[]
  isWorkbook?: boolean
  onJumpToEvidence: (index: number) => void
  /** Render every location and no fold toggle: the unit card already hides
   *  the whole list behind its own "n Fundstellen anzeigen" link. */
  showAll?: boolean
}

const COLLAPSED_COUNT = 3

/**
 * The statement's individual evidence locations. PDF statements read
 * `evidence`; workbook statements read `cells`, since workbook proofs never
 * cross the PDF evidence channel. Each location remains a real button so an
 * expanded list is fully keyboard-operable, not just more visible text.
 */
export function EvidenceList({
  evidence = [],
  cells = [],
  isWorkbook = false,
  onJumpToEvidence,
  showAll = false,
}: EvidenceListProps) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const anchors = isWorkbook ? cells : evidence

  if (anchors.length === 0) return null

  const visible =
    expanded || showAll ? anchors : anchors.slice(0, COLLAPSED_COUNT)

  return (
    <TooltipProvider delayDuration={200}>
      <div className="sv2-uslot sv2-dark space-y-1" data-testid="evidence-list">
        {visible.map((anchor, index) => {
          const quote = evidenceText(anchor, isWorkbook, translate)
          const location = evidenceLocation(anchor, isWorkbook, translate)
          const row = (
            <button
              type="button"
              // An unverified row's explanation is a Radix tooltip; a native
              // title on the same button would pop a SECOND bubble with a
              // different text. That row carries the full quote inside the
              // Radix content instead (review, wave 2).
              title={anchor.verified === false ? undefined : quote}
              className="group flex w-full cursor-pointer items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-left transition-colors hover:border-grey4 hover:bg-grey2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => onJumpToEvidence(index)}
            >
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-[12px] leading-4 text-white">
                  {quote}
                </span>
                {anchor.verified === false && (
                  <span className="mt-0.5 block text-[10px] text-grey5">
                    {translate("MC_DECL_V2_EVIDENCE_NOT_VERBATIM")}
                  </span>
                )}
              </span>
              <span className="font-mono flex-none pt-0.5 text-[10px] text-grey5 group-hover:text-white">
                {location}
              </span>
            </button>
          )
          // The explanation rides on the ROW (hover or focus), not on a second
          // focusable element nested inside the button — one tab stop per
          // location, the hint stays plain text, and its explanation is announced
          // with the row (review, wave 2). Neither branch may put a box
          // between the list and the button: the list's `space-y-1` gap is a
          // margin on each DIRECT child, and a `display: contents` wrapper
          // swallowed it (review, wave 2). Radix Tooltip/Trigger render
          // no DOM of their own; the verified branch uses a keyed Fragment.
          return anchor.verified === false ? (
            <Tooltip key={`${anchor.page}:${index}:${quote}`}>
              <TooltipTrigger asChild>{row}</TooltipTrigger>
              <TooltipContent side="top" className="max-w-[360px]">
                <span className="block">{quote}</span>
                <span className="mt-1 block text-grey5">
                  {translate("MC_DECL_V2_EVIDENCE_NOT_VERBATIM_TITLE")}
                </span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Fragment key={`${anchor.page}:${index}:${quote}`}>{row}</Fragment>
          )
        })}

        {anchors.length > COLLAPSED_COUNT && !showAll && (
          <button
            type="button"
            className="cursor-pointer px-2 py-1 text-[11px] font-semibold text-grey5 underline decoration-dotted underline-offset-4 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded
              ? translate("MC_DECL_V2_EVIDENCE_SHOW_LESS")
              : translate("MC_DECL_V2_EVIDENCE_SHOW_ALL", {
                  n: anchors.length,
                })}
          </button>
        )}
      </div>
    </TooltipProvider>
  )
}

function evidenceText(
  anchor: EvidenceAnchor | CellQuote,
  isWorkbook: boolean,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  const quote = anchor.quote?.trim()
  if (quote) return quote
  if ("summary" in anchor && anchor.summary?.trim())
    return anchor.summary.trim()
  if (isWorkbook && "sheet" in anchor && anchor.sheet && anchor.ref) {
    return translate("MC_DECL_WB_AT", {
      sheet: anchor.sheet,
      ref: anchor.ref,
    })
  }
  return translate("MC_DECL_V2_UNIT_CELL_JUMP")
}

function evidenceLocation(
  anchor: EvidenceAnchor | CellQuote,
  isWorkbook: boolean,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  if (isWorkbook && "sheet" in anchor && anchor.sheet && anchor.ref) {
    return translate("MC_DECL_WB_AT", {
      sheet: anchor.sheet,
      ref: anchor.ref,
    })
  }
  return translate("MC_DECL_V2_UNIT_PAGE_JUMP_SHORT", {
    page: anchor.page,
  })
}
